import type {
  FacetHit,
  Hit,
  MultipleQueriesQuery,
  MultipleQueriesResponse,
  SearchForFacetValuesQueryParams,
  SearchForFacetValuesResponse,
  SearchOptions,
} from '@algolia/client-search';
import type {
  ApolloClient,
  FetchPolicy,
  TypedDocumentNode,
} from '@apollo/client';
import { gql, useApolloClient } from '@apollo/client';
import type { SearchClient } from 'algoliasearch-helper/types/algoliasearch';
import { useMemo } from 'react';

import type {
  SearchFacetDefinitionInput,
  SearchRefinementInput,
  SearchSortInput,
} from '@sorare/core/src/__generated__/globalTypes';
import { SearchSortDirection } from '@sorare/core/src/__generated__/globalTypes';
import { IS_TEST_RUNNER } from '@sorare/core/src/config';
import type { PrintablePlayerSortName } from '@sorare/core/src/contexts/config';
import { SorareSearchClient } from '@sorare/core/src/hooks/search/useSearchClient';

import type {
  ExtractRightHandSide,
  extractQueryGeneric,
  extractVariableGeneric,
} from '#src/lib/searchClient.ts';

import type {
  usePrintablePlayersSearchClient_searchFacetValue,
  usePrintablePlayersSearchClient_searchPrintablePlayers,
} from './__generated__/usePrintablePlayersSearchClient.graphql.ts';

type FieldType = ExtractRightHandSide<PrintablePlayerSortName>;

export const FIELDS: Record<FieldType, string> = {
  // AVERAGE SCORE
  // ========================
  L10: 'so5.last_ten_played_so5_average_score',
  L15: 'so5.last_fifteen_so5_average_score',
  L5: 'so5.last_five_so5_average_score',
  season_avg: 'so5.season_average_score',
  L40: 'so5.last_forty_so5_average_score',

  // BREAKDOWN
  // ========================
  L15AA: 'so5.last_fifteen_average_all_around_score',
  L15D: 'so5.last_fifteen_average_decisive_score',
  L10AA: 'so5.last_ten_played_average_all_around_score',
  L10D: 'so5.last_ten_played_average_decisive_score',

  // COMMON
  // ========================
  name_for_sorting: 'player.name_for_sorting',
  average_mins_played: 'average_stats.mins_played',
  total_mins_played: 'total_stats.mins_played',
  next_daily_fixture_projected_score: 'next_daily_fixture_projected_score',
  next_classic_fixture_projected_score: 'next_classic_fixture_projected_score',
  next_classic_fixture_projected_grade: 'next_classic_fixture_projected_grade',
  next_daily_fixture_projected_grade: 'next_daily_fixture_projected_grade',
  gain_on_daily_projection: 'gain_on_daily_projection',
  gain_on_classic_projection: 'gain_on_classic_projection',

  // PRICE
  // ========================
  floor_prices_in_season_limited: 'floor_prices.in_season.limited',
  floor_prices_in_season_rare: 'floor_prices.in_season.rare',
  floor_prices_in_season_super_rare: 'floor_prices.in_season.super_rare',
  floor_prices_in_season_unique: 'floor_prices.in_season.unique',
  floor_prices_all_seasons_limited: 'floor_prices.all_seasons.limited',
  floor_prices_all_seasons_rare: 'floor_prices.all_seasons.rare',
  floor_prices_all_seasons_super_rare: 'floor_prices.all_seasons.super_rare',
  floor_prices_all_seasons_unique: 'floor_prices.all_seasons.unique',
  ib_prices_limited: 'ib_prices.limited',

  // POPULARITY
  // ========================
  last_thirty_days_subscription_count: 'last_thirty_days_subscription_count',

  // NBA
  // ========================
  points: 'average_stats.points',
  assists: 'average_stats.assists',
  rebounds: 'average_stats.rebounds',
  blocks: 'average_stats.blocks',
  double_double: 'average_stats.double_double',
  steals: 'average_stats.steals',
  three_points_made: 'average_stats.three_points_made',
  triple_double: 'average_stats.triple_double',
  turnovers: 'average_stats.turnovers',
  average_three_points_made: 'average_stats.three_points_made',

  // Baseball
  // ========================
  hitting_at_base: 'average_stats.hitting_at_base',
  hitting_at_plate: 'average_stats.hitting_at_plate',
  hitting_caught_stealing: 'average_stats.hitting_caught_stealing',
  hitting_doubles: 'average_stats.hitting_doubles',
  hitting_hit_by_pitch: 'average_stats.hitting_hit_by_pitch',
  hitting_home_runs: 'average_stats.hitting_home_runs',
  hitting_runs: 'average_stats.hitting_runs',
  hitting_runs_batted_in: 'average_stats.hitting_runs_batted_in',
  hitting_singles: 'average_stats.hitting_singles',
  hitting_stolen_bases: 'average_stats.hitting_stolen_bases',
  hitting_strikeouts: 'average_stats.hitting_strikeouts',
  hitting_triples: 'average_stats.hitting_triples',
  hitting_walks: 'average_stats.hitting_walks',
  pitching_earned_runs: 'average_stats.pitching_earned_runs',
  pitching_hits_allowed: 'average_stats.pitching_hits_allowed',
  pitching_hit_batsmen: 'average_stats.pitching_hit_batsmen',
  pitching_holds: 'average_stats.pitching_holds',
  pitching_innings_pitched: 'average_stats.pitching_innings_pitched',
  pitching_no_hitters: 'average_stats.pitching_no_hitters',
  pitching_pitch_count: 'average_stats.pitching_pitch_count',
  pitching_relief_appearance: 'average_stats.pitching_relief_appearance',
  pitching_saves: 'average_stats.pitching_saves',
  pitching_strikeouts: 'average_stats.pitching_strikeouts',
  pitching_walks: 'average_stats.pitching_walks',
  pitching_wins: 'average_stats.pitching_wins',

  // Football
  // ========================
  // PER 90'
  average_set_piece_taken: 'average_stats.set_piece_taken',
  average_goals: 'average_stats.goals',
  average_goal_assist: 'average_stats.goal_assist',
  average_assist_penalty_won: 'average_stats.assist_penalty_won',
  average_last_man_tackle: 'average_stats.last_man_tackle',
  average_clean_sheet: 'average_stats.clean_sheet',
  average_clean_sheet_60: 'average_stats.clean_sheet_60',
  average_yellow_card: 'average_stats.yellow_card',
  average_red_card: 'average_stats.red_card',
  average_error_lead_to_goal: 'average_stats.error_lead_to_goal',
  average_penalty_conceded: 'average_stats.penalty_conceded',
  average_three_goals_conceded: 'average_stats.three_goals_conceded',
  average_own_goals: 'average_stats.own_goals',
  average_game_started: 'average_stats.game_started',
  average_won_tackle: 'average_stats.won_tackle',
  average_interception_won: 'average_stats.interception_won',
  average_accurate_pass: 'average_stats.accurate_pass',
  // ========================
  // LAST 15 TOTAL
  total_set_piece_taken: 'total_stats.set_piece_taken',
  total_goals: 'total_stats.goals',
  total_goal_assist: 'total_stats.goal_assist',
  total_assist_penalty_won: 'total_stats.assist_penalty_won',
  total_last_man_tackle: 'total_stats.last_man_tackle',
  total_clean_sheet: 'total_stats.clean_sheet',
  total_clean_sheet_60: 'total_stats.clean_sheet_60',
  total_yellow_card: 'total_stats.yellow_card',
  total_red_card: 'total_stats.red_card',
  total_error_lead_to_goal: 'total_stats.error_lead_to_goal',
  total_penalty_conceded: 'total_stats.penalty_conceded',
  total_three_goals_conceded: 'total_stats.three_goals_conceded',
  total_own_goals: 'total_stats.own_goals',
  total_game_started: 'total_stats.game_started',
  total_won_tackle: 'total_stats.won_tackle',
  total_interception_won: 'total_stats.interception_won',
  total_accurate_pass: 'total_stats.accurate_pass',
  // ========================
  // OTHERS
  starter_odds_basis_points: 'starter_odds_basis_points',
  projected_grade: 'projected_grade',
};

type BaseFacetQueryVariables = {
  query: string;
  field: string;
  facetQuery: string;
  advancedFilters?: string | null;
  limit?: number;
  refinements: SearchRefinementInput[];
  onlyFollowed?: boolean;
};
// Helper to decrement the recursion counter
type Prev = [never, 0, 1, 2, 3, 4, 5];

type ContainsSearchPlayersFacetValues<
  T,
  Depth extends number = 5,
> = Depth extends 0
  ? false
  : T extends {
        searchPlayerFacetValues: usePrintablePlayersSearchClient_searchFacetValue[];
      }
    ? true
    : T extends object
      ? {
          [K in keyof T]: ContainsSearchPlayersFacetValues<T[K], Prev[Depth]>;
        }[keyof T]
      : false;

type MustContainsSearchPlayersFacetValues<T> =
  ContainsSearchPlayersFacetValues<T> extends true ? T : never;

type BaseFacetGenericQuery<
  TQuery extends object,
  Variables extends BaseFacetQueryVariables,
> = TypedDocumentNode<MustContainsSearchPlayersFacetValues<TQuery>, Variables>;

export type BaseQueryVariables = {
  query: string;
  page: number;
  pageSize: number;
  sorts: SearchSortInput[];
  advancedFilters?: string | null;
  refinements: SearchRefinementInput[];
  facets: SearchFacetDefinitionInput[];
  onlyFollowed?: boolean;
};

type ContainsSearchPlayers<T, Depth extends number = 5> = Depth extends 0
  ? false
  : T extends {
        searchPlayers: usePrintablePlayersSearchClient_searchPrintablePlayers;
      }
    ? true
    : T extends object
      ? {
          [K in keyof T]: ContainsSearchPlayers<T[K], Prev[Depth]>;
        }[keyof T]
      : false;

export type MustContainSearchPlayers<T> =
  ContainsSearchPlayers<T> extends true ? T : never;

export type BaseGenericQuery<
  TQuery extends object,
  Variables extends BaseQueryVariables,
> = TypedDocumentNode<MustContainSearchPlayers<TQuery>, Variables>;

class PrintablePlayersSearchClient<
  TQuery extends BaseGenericQuery<object, TVariables>,
  TFacetQuery extends BaseFacetGenericQuery<object, TFacetVariables>,
  TVariables extends BaseQueryVariables = extractVariableGeneric<TQuery>,
  TFacetVariables extends BaseFacetQueryVariables =
    extractVariableGeneric<TFacetQuery>,
>
  extends SorareSearchClient
  implements SearchClient
{
  private client: ApolloClient<object>;

  private query: TQuery;

  private options: {
    onlyFollowed: boolean;
    variables?: Record<string, unknown>;
    fetchPolicy?: FetchPolicy;
    skip?: boolean;
  };

  private fQuery: TFacetQuery;

  private needsComposeTeamBench: boolean;

  private extractSearchPlayers: (
    data: extractQueryGeneric<MustContainSearchPlayers<TQuery>>
  ) => usePrintablePlayersSearchClient_searchPrintablePlayers | undefined;

  private extractSearchPlayersFacetValues: (
    data: extractQueryGeneric<MustContainsSearchPlayersFacetValues<TFacetQuery>>
  ) => usePrintablePlayersSearchClient_searchFacetValue[] | undefined;

  constructor(
    client: ApolloClient<object>,
    query: TQuery,
    options: {
      onlyFollowed: boolean;
      needsComposeTeamBench?: boolean;
      variables?: Record<string, unknown>;
      fetchPolicy?: FetchPolicy;
      skip?: boolean;
    },
    extractSearchPlayers: (
      data: extractQueryGeneric<MustContainSearchPlayers<TQuery>>
    ) => usePrintablePlayersSearchClient_searchPrintablePlayers | undefined,
    facetOptions: {
      extractSearchPlayersFacetValues: (
        data: extractQueryGeneric<
          MustContainsSearchPlayersFacetValues<TFacetQuery>
        >
      ) => usePrintablePlayersSearchClient_searchFacetValue[] | undefined;
      fQuery: TFacetQuery;
    }
  ) {
    super();
    this.client = client;
    this.query = query;
    this.options = options;
    this.extractSearchPlayers = extractSearchPlayers;
    this.fQuery = facetOptions.fQuery;
    this.needsComposeTeamBench = !!options.needsComposeTeamBench;
    this.extractSearchPlayersFacetValues =
      facetOptions.extractSearchPlayersFacetValues;
  }

  private toSorts(indexName: string): SearchSortInput[] {
    const sorts = indexName
      .split(',')
      .map(index => {
        const [direction, ...nameParts] = index.split('-');
        const name = nameParts.join('-');

        const field =
          FIELDS[name as FieldType] ||
          (Object.entries(FIELDS).find(([key]) => name.startsWith(key)) &&
            name);

        if (field) {
          return {
            field,
            direction:
              direction === 'ASC'
                ? SearchSortDirection.ASC
                : SearchSortDirection.DESC,
          };
        }

        return null;
      })
      .filter(Boolean);

    return sorts;
  }

  async search<THit>(queries: readonly MultipleQueriesQuery[]) {
    // only the main query is important, all others are for the disjunctive facets and are handled natively
    const query = queries[0];
    // Handles empty queries in tests
    // cf https://stackoverflow.com/questions/66483928/instantsearch-js-with-error-in-javascript-console-rangeerror-invalid-array-l
    if (
      IS_TEST_RUNNER ||
      query.params?.hitsPerPage === 0 ||
      // Skip the search if the skip option is true
      this.options.skip
    ) {
      const res: MultipleQueriesResponse<THit> = {
        results: queries.map(() => ({
          hits: [] as Hit<THit>[],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          facets: {},
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: '',
          params: '',
        })),
      };
      return Promise.resolve(res);
    }

    const params = query.params!;
    const { data } = await this.client.query<
      extractQueryGeneric<MustContainSearchPlayers<TQuery>>,
      TVariables
    >({
      query: this.query,
      fetchPolicy: this.options.fetchPolicy,
      variables: {
        query: params.query || '',
        page: (params.page || 0) + 1, // 1-based
        pageSize: params.hitsPerPage || 1,
        sorts: this.toSorts(query.indexName),
        advancedFilters: params.filters,
        refinements: this.toRefinements(
          params.facetFilters as string[][],
          params.numericFilters as string[] | undefined
        ),
        facets: this.toFacets(params.facets),
        onlyFollowed: this.options.onlyFollowed,
        ...(this.options.variables || {}),
      } as TVariables,
    });

    const res = this.extractSearchPlayers(data);

    const hits = this.needsComposeTeamBench ? res?.hits : res?.commonPlayerHits;

    const response: MultipleQueriesResponse<THit> = {
      results: [
        {
          hits:
            hits?.map(hit => ({
              ...(hit as THit),
              slug: hit.anyPlayer.slug,
              objectID: hit.anyPlayer.slug,
            })) || [],
          facets: Object.fromEntries(
            res?.facets.map(facet => [
              facet.field,
              Object.fromEntries(facet.values.map(v => [v.value, v.count])),
            ]) || []
          ),
          facets_stats: Object.fromEntries(
            res?.facets.map(facet => [
              facet.field,
              {
                min: parseInt(facet.min?.value || '0', 10),
                max: parseInt(facet.max?.value || '0', 10),
                avg: 0,
                sum: 0,
              },
            ]) || []
          ),
          page: res?.page || 0,
          nbPages: res?.nbPages || 0,
          nbHits: res?.nbHits || 0,
          hitsPerPage: params.hitsPerPage!,
          processingTimeMS: 0,
          query: params.query!,
          exhaustiveNbHits: true,
          params: '',
          index: query.indexName,
        },
      ],
    };

    return response;
  }

  async searchForFacetValues(
    queries: ReadonlyArray<{
      readonly indexName: string;
      readonly params: SearchForFacetValuesQueryParams & SearchOptions;
    }>
  ) {
    // only the first query is handled - which should cover all use-cases
    const query = queries[0];
    const params = query.params!;

    const { data } = await this.client.query<
      extractQueryGeneric<MustContainsSearchPlayersFacetValues<TFacetQuery>>,
      TFacetVariables
    >({
      query: this.fQuery,
      fetchPolicy: this.options.fetchPolicy,
      variables: {
        query: params.query || '',
        field: params.facetName,
        facetQuery: params.facetQuery || '',
        advancedFilters: params.filters,
        refinements: this.toRefinements(
          params.facetFilters as string[][],
          params.numericFilters as string[]
        ),
        onlyFollowed: this.options.onlyFollowed,
      } as TFacetVariables,
    });
    const searchPlayerFacetValues = this.extractSearchPlayersFacetValues(data);

    const facetHits: FacetHit[] =
      searchPlayerFacetValues?.map(v => ({
        value: v.value,
        count: v.count,
        highlighted: '',
      })) || [];

    const response: SearchForFacetValuesResponse[] = [
      {
        facetHits,
        exhaustiveFacetsCount: true,
      },
    ];
    return response;
  }
}

export const usePrintablePlayersSearchClient = <
  TQuery extends BaseGenericQuery<MustContainSearchPlayers<object>, TVariables>,
  TFacetQuery extends BaseFacetGenericQuery<
    MustContainsSearchPlayersFacetValues<object>,
    TFacetVariables
  >,
  TVariables extends BaseQueryVariables = extractVariableGeneric<TQuery>,
  TFacetVariables extends BaseFacetQueryVariables =
    extractVariableGeneric<TFacetQuery>,
>(
  query: TQuery,
  options: {
    needsComposeTeamBench?: boolean;
    onlyFollowed: boolean;
    variables?: Record<string, unknown>;
    fetchPolicy?: FetchPolicy;
    skip?: boolean;
  },
  extractSearchPlayers: (
    data: extractQueryGeneric<MustContainSearchPlayers<TQuery>>
  ) => usePrintablePlayersSearchClient_searchPrintablePlayers | undefined,
  facetOptions: {
    fQuery: TFacetQuery;
    extractSearchPlayersFacetValues: (
      data: extractQueryGeneric<
        MustContainsSearchPlayersFacetValues<TFacetQuery>
      >
    ) => usePrintablePlayersSearchClient_searchFacetValue[] | undefined;
  }
): SearchClient => {
  const apolloClient = useApolloClient();
  const optionsString = JSON.stringify(options);

  const searchClient: SearchClient = useMemo(() => {
    return new PrintablePlayersSearchClient<
      TQuery,
      TFacetQuery,
      TVariables,
      TFacetVariables
    >(apolloClient, query, options, extractSearchPlayers, facetOptions);
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [apolloClient, query, optionsString, extractSearchPlayers]);

  return searchClient;
};

usePrintablePlayersSearchClient.fragments = {
  searchPrintablePlayers: gql`
    fragment usePrintablePlayersSearchClient_searchPrintablePlayers on SearchPlayers {
      page
      nbPages
      nbHits
      hits {
        id
        anyPlayer {
          slug
        }
      }
      commonPlayerHits {
        id
        anyPlayer {
          slug
        }
      }
      facets {
        field
        values {
          value
          count
        }
        min {
          value
        }
        max {
          value
        }
      }
      processingTimeMs {
        facetValuesFts
        faceting
        filtering
        fts
        sorting
        total
      }
    }
  ` as TypedDocumentNode<usePrintablePlayersSearchClient_searchPrintablePlayers>,
  searchFacetValue: gql`
    fragment usePrintablePlayersSearchClient_searchFacetValue on SearchFacetValue {
      value
      count
    }
  ` as TypedDocumentNode<usePrintablePlayersSearchClient_searchFacetValue>,
};
